import { Box, Button, Popover, PopoverProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

const Root = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  borderRadius: '8px',
  overflow: 'hidden',
  minWidth: 0,
});

const PrimarySegment = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minWidth: 0,
  padding: '4px 12px',
  borderRadius: 0,
  textTransform: 'none',
  transition: 'background-color 0.15s ease',
});

const StyledLabel = styled('span')({
  fontWeight: 700,
  fontSize: 12,
});

const DividerBox = styled(Box)(({ theme }) => ({
  width: '1px',
  alignSelf: 'stretch',
  minHeight: 32,
  backgroundColor: theme.palette.primary.contrastText,
  opacity: 0.5,
  flexShrink: 0,
}));

const MenuSegment = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 32,
  minWidth: 32,
  flexShrink: 0,
  padding: 0,
  borderRadius: 0,
  color: theme.palette.primary.contrastText,
  transition: 'background-color 0.15s ease',
  ...(isActive && {
    backgroundColor: theme.palette.primary.dark,
  }),
  '&::after': {
    content: '" "',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.grey[0],
    opacity: 0.05,
  },
}));

const StyledChevron = styled(ChevronDown, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'relative',
  transition: 'transform 0.2s ease-in-out',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  zIndex: 1,
}));

export interface MenuButtonProps {
  label: string | React.ReactNode;
  loading?: boolean;
  children?: React.ReactNode | ((props: { close: () => void; isOpen: boolean }) => React.ReactNode);
  popoverProps?: Omit<PopoverProps, 'open' | 'anchorEl' | 'onClose'>;
  /**
   * Click handler for the left (label) segment. Does not open the menu.
   */
  onPrimaryClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Click handler for the chevron segment — use with an external menu (e.g. PopoverMenu `renderTrigger`).
   */
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * When the dropdown is controlled outside (e.g. PopoverMenu `renderTrigger` `open`), pass it so the chevron
   * segment stays visually active while open.
   */
  menuOpen?: boolean;
}

const MenuButton: React.FC<MenuButtonProps> = ({
  label,
  loading = false,
  children,
  popoverProps,
  onPrimaryClick,
  onMenuClick,
  menuOpen = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isInternalOpen = Boolean(anchorEl);
  const menuIsOpen = isInternalOpen || menuOpen;
  const hasInternalMenu = React.Children.count(children) > 0 || typeof children === 'function';
  const showMenuSegment = hasInternalMenu || Boolean(onMenuClick);

  const handlePrimaryClick = (event: React.MouseEvent<HTMLElement>) => {
    onPrimaryClick?.(event);
  };

  const handleMenuSegmentClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (hasInternalMenu) {
      setAnchorEl(event.currentTarget);
    }
    onMenuClick?.(event);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Root
        role='group'
        data-testid='menu-button-root'
        sx={{
          ...(showMenuSegment ? {} : { display: 'inline-flex' }),
        }}
      >
        <PrimarySegment
          type='button'
          variant='contained'
          onClick={handlePrimaryClick}
          loading={loading}
          data-testid='menu-button-primary'
          disableRipple
        >
          <StyledLabel>{label}</StyledLabel>
        </PrimarySegment>

        {showMenuSegment && (
          <>
            <DividerBox aria-hidden />
            <MenuSegment
              type='button'
              variant='contained'
              onClick={handleMenuSegmentClick}
              isActive={menuIsOpen}
              aria-haspopup={hasInternalMenu || onMenuClick ? 'true' : undefined}
              aria-expanded={
                showMenuSegment && (hasInternalMenu || onMenuClick) ? menuIsOpen : undefined
              }
              aria-label='Open menu'
              data-testid='menu-button-menu'
              disableRipple
            >
              <StyledChevron isOpen={menuIsOpen} size={16} />
            </MenuSegment>
          </>
        )}
      </Root>

      {hasInternalMenu && (
        <Popover
          open={isInternalOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              elevation: 1,
              sx: { mt: 1, border: '1px solid', borderColor: 'divider', minWidth: '240px' },
            },
          }}
          {...popoverProps}
        >
          {typeof children === 'function'
            ? children({ close: handleClose, isOpen: isInternalOpen })
            : children}
        </Popover>
      )}
    </>
  );
};

export default MenuButton;
